import type { FunctionalComponent } from '@bpmn-io/properties-panel/preact';
import { useEffect, useMemo, useRef } from '@bpmn-io/properties-panel/preact/hooks';
import { jsx } from '@bpmn-io/properties-panel/preact/jsx-runtime';
import { createRoot, type Root } from 'react-dom/client';
import { useService } from '../hooks/useService';
import { renderPeoplePicker, type TPeoplePickerOption } from '../../../../pickers/PeoplePicker';

interface IMailRecipientProps {
    // attrPropName: string;
    element: any;
    id: string;
    label: string;
    moddleElement: any;
    // label: string;
    type: RecipientType;
    // valPropName: string;
}

enum RecipientType {
    CC = 'cc',
    TO = 'to',
}

const attrPropNameByType = (type: RecipientType) => {
    switch (type) {
        case RecipientType.CC:
            return 'copyto';
        case RecipientType.TO:
            return 'sendto';
    }
};

const schemaNameByType = (type: RecipientType) => {
    const prefix = 'fism:Mail';
    switch (type) {
        case RecipientType.CC:
            return prefix + 'CopyTo';
        case RecipientType.TO:
            return prefix + 'SendTo';
    }
};

export const MailRecipient: FunctionalComponent<IMailRecipientProps> = (props) => {
    const { element, label, moddleElement, type } = props;
    const bpmnFactory = useService('bpmnFactory');
    const modeling = useService('modeling');
    const translate = useService('translate');
    const siteUrl: string | undefined =
        useService('canvas').getRootElement().businessObject.siteUrl;
    const containerRef = useRef<HTMLDivElement>();
    const rootRef = useRef<Root>();
    const attrPropName = attrPropNameByType(type);
    const attrPropValue: TPeoplePickerOption[] | undefined = (moddleElement.get(attrPropName) || {})
        .values;

    const recipients = useMemo<TPeoplePickerOption[]>(
        () =>
            Array.isArray(attrPropValue)
                ? attrPropValue.map(({ email, memberId, loginName, name }) => ({
                      email,
                      memberId,
                      loginName,
                      name,
                  }))
                : [],
        [attrPropValue]
    );

    useEffect(() => {
        const root = rootRef.current || createRoot(containerRef.current!);
        rootRef.current = root;
        renderPeoplePicker(root, {
            label,
            onChange: (value) => {
                if (value.length > 0) {
                    const schemaName = schemaNameByType(type);
                    // create/override (to || cc) and update
                    modeling.updateModdleProperties(element, moddleElement, {
                        [attrPropName]: bpmnFactory.create(schemaName, {
                            values: value.map(({ email, memberId, loginName, name }) =>
                                bpmnFactory.create('fism:MailRecipient', {
                                    email,
                                    memberId,
                                    loginName,
                                    name,
                                })
                            ),
                        }),
                    });
                } else if (moddleElement[attrPropName]) {
                    // remove (to || cc) and update
                    modeling.updateModdleProperties(element, moddleElement, {
                        [attrPropName]: undefined,
                    });
                }
            },
            principalType: 1,
            siteUrl,
            translate,
            value: recipients,
        });
    }, [label, siteUrl, recipients]);

    useEffect(
        () => () => {
            if (rootRef.current) {
                rootRef.current.unmount();
            }
        },
        []
    );

    return useMemo(
        () =>
            // @ts-ignore
            jsx('div', {
                className: 'bio-properties-panel-entry',
                // 'data-entry-id': id,
                // id,
                ref: containerRef,
            }),
        []
    );
};
